import React, { useRef, useState } from "react";
import { connect } from "react-redux";
import { Icon } from "@clarkinc/zest-design-system/ui";
import MenuDropdown from "../MenuDropdown";
import MenuItem from "../MenuItem";
import useMenuHandlers from "~/common/Header/hooks/useMenuHandlers";
import { ChatState, UserState, RootState } from "~/common/Store/models/RootReadonly";
import PendingOrders from "./PendingOrders";
import AccountIcon from "../Icons/AccountIcon";
import OrderIcon from "../Icons/OrderIcon";
import ChevronDownIcon from "../Icons/ChevronDownIcon";
import sharedButtonStyles from "../sharedButtonStyles";
import Icons from "~/common/Icons/Standard";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import useExpiringCard from "../../../../my-account/Common/ExpiringCreditCards/useExpiringCard";
interface Props {
  mounted: boolean;
  chat: ChatState;
  user: UserState;
  checkout: boolean;
}
const LoggedIn = ({
  user,
  checkout
}: Props) => {
  const [accountButtonActive, setAccountButtonActive] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const accountButtonRef = useRef<HTMLButtonElement>(null);
  const accountMenuLiRef = useRef<HTMLLIElement>(null);
  const {
    alertType
  } = useExpiringCard({
    guid: user.guid
  });
  const getIconColor = () => {
    switch (alertType) {
      case "MonthSingle":
      case "MonthMulti":
        return "#00AEEF";
      // blue

      case "WeekSingle":
      case "WeekMulti":
        return "#B16818";
      // orange

      case "MultipleCurrentlyExpired":
      case "CurrentlyExpired":
        return "#D0021B";
      // red

      default:
        return undefined;
    }
  };
  const {
    closeMenus,
    handleEscape,
    toggleMenus,
    handleBlur
  } = useMenuHandlers(setAccountButtonActive, accountMenuLiRef, accountButtonRef);
  useOnClickOutside(accountMenuRef, accountButtonRef, closeMenus);
  const iconColor = getIconColor();
  return <ul className="hidden items-center m-0 lt:flex lt:ml-2 lg:ml-8 xl:ml-[104px]" data-sentry-component="LoggedIn" data-sentry-source-file="index.tsx">
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <li className={`list-none relative ${checkout ? "mr-4" : ""}`} onBlur={handleBlur} ref={accountMenuLiRef} onKeyUp={e => {
      if (e.key === "Escape") {
        handleEscape();
      }
    }}>
        <button type="button" ref={accountButtonRef} onClick={toggleMenus} className={`${sharedButtonStyles} ${accountButtonActive ? "bg-gray-200" : "bg-transparent"}`} data-testid="loggedin-user-account-dropdown" aria-expanded={accountButtonActive}>
          {alertType ? <Icon className="mr-0.5" name="ri-error-warning-fill" size="xsmall" style={{
          fill: iconColor
        }} theme="wss" /> : <AccountIcon classNames="pr-1 self-center" style={{
          minWidth: "12px",
          width: "12px",
          height: "16px"
        }} />}

          <span className="leading-none">
            <span className="max-w-16 capitalize truncate block text-xs font-normal text-left leading-none">
              {user.firstName}&#39;s
            </span>
            <span className="flex items-center text-sm leading-none">
              Account
              <ChevronDownIcon classNames="pl-1 text-gray-800 fill-current" data-sentry-element="ChevronDownIcon" data-sentry-source-file="index.tsx" />
            </span>
          </span>
        </button>
        <MenuDropdown active={accountButtonActive} isCheckout={checkout} data-sentry-element="MenuDropdown" data-sentry-source-file="index.tsx">
          <div className={`${accountButtonActive ? "block" : "hidden"} grid grid-cols-2`} ref={accountMenuRef}>
            <div className="flex flex-col px-2-1/2 py-2">
              <div className="flex items-center pl-2 pr-4 py-1-1/2">
                <Icons name="rounded-home" className="w-5 h-4" data-sentry-element="Icons" data-sentry-source-file="index.tsx" />
                <p className="ml-2 m-0 text-sm font-bold leading-4" id="my-account">
                  My Account
                </p>
              </div>
              <ul aria-labelledby="my-account" className="flex-grow flex flex-col flex-1 list-none ml-0 mb-0 p-0">
                <MenuItem content="Account" href="/myaccount/" name="my-account" data-sentry-element="MenuItem" data-sentry-source-file="index.tsx" />
                <MenuItem content="Orders" href="/myaccount/orders/" name="orders" data-sentry-element="MenuItem" data-sentry-source-file="index.tsx" />
                <MenuItem content="Auto Reorder" href="/myaccount:autoreorder/" name="auto-reorder" data-sentry-element="MenuItem" data-sentry-source-file="index.tsx" />
                <MenuItem content="Returns" href="/myaccount/returns/" name="returns" data-sentry-element="MenuItem" data-sentry-source-file="index.tsx" />
                <MenuItem content="Shipping" href="/myaccount/shipping/?returnUrl=" name="shipping" data-sentry-element="MenuItem" data-sentry-source-file="index.tsx" />
                <MenuItem content="Billing" href="/myaccount/payment/" name="billing" data-sentry-element="MenuItem" data-sentry-source-file="index.tsx" />
                {user.quotes && <MenuItem name="quotes" content="Your Quotes" href="/manage-quotes/" />}
                {Boolean(user.pendingOrders) && <MenuItem name="pending-orders" content={<PendingOrders pendingOrders={user.pendingOrders} />} href="/revieworder.html" />}
                <MenuItem divider content="Log Out" href="/myaccount/?logout=Y" name="log-out" extraListClasses="mt-auto" data-sentry-element="MenuItem" data-sentry-source-file="index.tsx" />
              </ul>
            </div>
            <div className="px-2-1/2 py-2 bg-gray-50 border-0 border-l border-solid border-gray-100 rounded-r">
              <div className="flex items-center pl-2 pr-4 py-1-1/2">
                <Icons name="multiple-files" className="w-[13px] h-4" data-sentry-element="Icons" data-sentry-source-file="index.tsx" />
                <p className="ml-2 m-0 text-sm font-bold leading-4" id="lists">
                  Lists
                </p>
              </div>
              <ul className="list-none ml-0 mb-0 p-0" aria-labelledby="lists">
                <MenuItem name="wish-list" content="Wish Lists" href="/wishlist.html" data-sentry-element="MenuItem" data-sentry-source-file="index.tsx" />
                <MenuItem content="Rapid Reorder" href="/reorder.html" name="rapid-reorder" data-sentry-element="MenuItem" data-sentry-source-file="index.tsx" />
                {user.orderGuides && <MenuItem name="order-guides" content="Order Guides" href="/myproducts.html" />}
              </ul>
              <div className="pt-4">
                <div className="flex items-center pl-2 pr-4 py-1-1/2">
                  <Icons name="bookmark" className="w-3 h-4" data-sentry-element="Icons" data-sentry-source-file="index.tsx" />
                  <p className="ml-2 m-0 text-sm font-bold leading-4" id="quick-links">
                    Quick Links
                  </p>
                </div>
                <ul aria-labelledby="my-account" className="flex-grow flex flex-col flex-1 list-none ml-0 mb-0 p-0">
                  <MenuItem content="WebstaurantPlus" href={user.hasWebPlus || alertType ? "/myaccount/plus/" : "/plus/"} name="webstaurant-plus" renderIcon={!!iconColor} iconProps={{
                  name: "ri-error-warning-fill",
                  style: {
                    fill: iconColor
                  },
                  size: "xsmall"
                }} data-sentry-element="MenuItem" data-sentry-source-file="index.tsx" />
                  <MenuItem content="Rewards" href={user.isRewardsMember ? "/myaccount/payment/#credits" : "/rewards/"} name="webstaurant-rewards" data-sentry-element="MenuItem" data-sentry-source-file="index.tsx" />
                  <MenuItem content="Get the App" href="/services/app/" name="webstaurant-app-quick-link" data-sentry-element="MenuItem" data-sentry-source-file="index.tsx" />
                  <MenuItem content="VersaHub" href="/versahub#/" name="versahub" data-sentry-element="MenuItem" data-sentry-source-file="index.tsx" />
                </ul>
              </div>
            </div>
          </div>
        </MenuDropdown>
      </li>
      {!checkout && <li className="list-none pr-4 ml-1 hidden lt:block">
          <a href="/myaccount/orders/" className={sharedButtonStyles} data-testid="order-btn">
            <OrderIcon classNames="pr-1" style={{
          minWidth: "16px",
          width: "16px",
          height: "16px"
        }} />
            <span className="leading-none">
              <span className="block text-xs leading-none font-normal text-left">
                Returns &#38;
              </span>
              <span className="flex items-center text-sm leading-none">
                Orders
              </span>
            </span>
          </a>
        </li>}
    </ul>;
};
export const mapStateToProps = (state: RootState, ownProps: Props) => {
  if (!ownProps.mounted) return ownProps;
  return state;
};
export default connect(mapStateToProps)(LoggedIn);